import { useCallback, useEffect, useState } from "react";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Box, Button, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { AddExpenseFormDialog, ExpenseDialog } from "@/features/expenses";
import { UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatDate, formatDateTime, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const Expenses = () => {
  const { hasPermission, outlet, changeOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [expenseId, setExpenseId] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const loadExpenses = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("expenses", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          withUser: true,
          withExpenseCategory: true,
        },
      });
      setExpenses(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat biaya"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_EXPENSES) && (
    <Button variant="contained" onClick={() => setIsAddDialogOpen(true)}>
      Tambah
    </Button>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <UserOutletsSelect value={outlet} onChange={changeOutlet} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <SearchTextField
                fullWidth
                placeholder="Cari nomor referensi"
                {...searchProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12, lg: 6 }}>
              <CustomDateRangePicker {...dateRangeProps} />
            </Grid>
          </Grid>
        </Box>
        <DataGrid
          rows={expenses}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor Referensi",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal Transaksi",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => formatDate(value),
            },
            {
              field: "createdAt",
              headerName: "Dibuat pada",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => formatDateTime(value),
            },
            {
              field: "user",
              headerName: "Dibuat oleh",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => value?.name,
            },
            {
              field: "expenseCategory",
              headerName: "Kategori",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => value?.name,
            },
            {
              field: "amount",
              headerName: "Nominal",
              flex: 1,
              type: "number",
              sortable: false,
              valueGetter: (value) => formatNumber(value),
            },
            ...(hasPermission(PermissionEnum.READ_EXPENSES)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="view"
                        onClick={() => setExpenseId(params.id)}
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        {isAddDialogOpen && (
          <AddExpenseFormDialog
            onClose={() => setIsAddDialogOpen(false)}
            onSubmitted={loadExpenses}
          />
        )}
        {!!expenseId && (
          <ExpenseDialog
            expenseId={expenseId}
            onClose={() => setExpenseId(null)}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
